import { convertRowsToLocalDateTimes } from "services/utils/sortDates";
import { BaseEventType, EventsByResourceType, EventsListType, EventType, ResourceGroupType } from "../SchedulingTypes";
import { calculateDurationMinutesFromDateTimes, determineNearestTimeSlot, generateDayKey, generateTimeKeyFromDateTimeString } from "./schedulingDateUtils";
import { calculateOverlappingEvents } from "./calculateOverlappingEvents";

// Generate scheduler events from the fetched event data
export const generateEvents = (
    data: {
        appointments: any[],
        timerows: any[],
        activities: any[],
        activity_recurrence_ids?: number[],
    },
    userLocale: string,
    userTimezone: string,
    renderTimeSlots = false,
    days: Date[],
    resourceGroups: ResourceGroupType[],
    existingEvents: EventsListType = {},
    existingOverlapMap: Map<string, EventType[]> = new Map(),
    isDeltaUpdate = false,
): { 
    generatedEvents: EventsListType; 
    eventsByResource: EventsByResourceType;
    updatedOverlapMap: Map<string, EventType[]>;
} => {
    // Initialize event variables
    const newEvents: EventsListType = {};
    const resourceMap: EventsByResourceType = {};

    // Generate a set of day keys of the current fetched period
    const currentFetchPeriod = new Set(days.map(d => generateDayKey(d)));

    // Convert datetimes from utc to local timezone
    const { rowsWithLocalDatetimes: localAppointments } = convertRowsToLocalDateTimes(
        data.appointments, 'start_datetime', 'end_datetime', userLocale, userTimezone
    );    
    const { rowsWithLocalDatetimes: localTimerows } = convertRowsToLocalDateTimes(
        data.timerows, 'start_datetime', 'end_datetime', userLocale, userTimezone
    );
    const { rowsWithLocalDatetimes: localActivities } = convertRowsToLocalDateTimes(
        data.activities, 'start_datetime', 'end_datetime', userLocale, userTimezone
    );

    // Combine all converted events to one array
    const localTimezoneEvents = [
        ...localAppointments.map(r => ({ ...r, type: 'appointment' })),
        ...localTimerows.map(r => ({ ...r, type: 'timerow' })),
        ...localActivities.map(r => ({ ...r, type: 'activity' })),
    ];

    // Loop through the events to generate objects
    for (const item of localTimezoneEvents) {

        // Get the event data
        const { type, id, resource_ids = [], recurrence_id, occurrence_date } = item;

        // Generate a separated event for each resource
        for (const resId of resource_ids) {

            // Generate event key, a virtual occurrence has no id so the recurrence and date identify it instead
            const key = recurrence_id
                ? `activity-recurrence-${recurrence_id}-${occurrence_date}-${resId}`
                : `${type}-${id}-${resId}`;

            // Create base event object
            const base: BaseEventType = {
                key,
                type,
                resourceId: resId.toString(),
                startDateTime: item.start_datetime,
                endDateTime: item.end_datetime,
                timezone: item.timezone ?? undefined,
                dayKey: generateDayKey(new Date(item.start_datetime)),
                startTime: generateTimeKeyFromDateTimeString(item.start_datetime),
                endTime: generateTimeKeyFromDateTimeString(item.end_datetime),
                durationMinutes: calculateDurationMinutesFromDateTimes(item.start_datetime, item.end_datetime),
                deleted: item.deleted ?? false,
                ...(renderTimeSlots && { timeSlot: determineNearestTimeSlot(item.start_datetime) })
            };

            // Initialize event type object
            let event: EventType;

            // Create appointment event object
            if (type === "appointment") {
                event = {
                    ...base,
                    type: "appointment",
                    appointmentId: id,
                    status: item.status ?? "",
                    custom_color: item.custom_color ?? null,
                    locked: item.locked ?? false,

                    // Resolve the running timer color for this specific resource row
                    running_timer: item.running_timer_resources?.[String(resId)] ?? null,
                    fallback_status_condition: item.fallback_status_condition ?? null,
                    job: item.job && {
                        id: item.job.id,
                        client_name: item.job.client_name ?? undefined,
                        type_name: item.job.type_name ?? undefined,
                        type_color: item.job.type_color ?? null,
                        work_location_street: item.job.work_location_street ?? undefined,
                        work_location_house_number: item.job.work_location_house_number ?? undefined,
                        work_location_postal_code: item.job.work_location_postal_code ?? undefined,
                        work_location_city: item.job.work_location_city ?? undefined,
                        work_location_latitude: item.job.work_location_latitude ?? null,
                        work_location_longitude: item.job.work_location_longitude ?? null,
                        work_location_geocode_status: item.job.work_location_geocode_status ?? 'not_attempted',
                        internal_reference: item.job.internal_reference ?? null,
                        external_reference: item.job.external_reference ?? null,
                        number: item.job.number ?? null,
                        status_condition: item.job.status_condition ?? null,
                        status_name: item.job.status_name ?? null,
                        resolution: item.job.resolution ?? null,
                        main_description: item.job.main_description ?? null,
                        project_number: item.job.project_number ?? null,
                        project_name: item.job.project_name ?? null,
                        labels: item.job.labels ?? null,
                    }
                };

            // Create timerow event object
            } else if (type === "timerow") {
                event = {
                    ...base,
                    type: "timerow",
                    timerowId: id,
                    appointmentId: item.appointment_id ?? null,
                    fallback_status_condition: item.fallback_status_condition ?? null,
                    job: item.job && {
                        id: item.job.id,
                        client_name: item.job.client_name ?? undefined,
                        type_name: item.job.type_name ?? undefined,
                        type_color: item.job.type_color ?? null,
                        work_location_street: item.job.work_location_street ?? undefined,
                        work_location_house_number: item.job.work_location_house_number ?? undefined,
                        work_location_postal_code: item.job.work_location_postal_code ?? undefined,
                        work_location_city: item.job.work_location_city ?? undefined,
                        work_location_latitude: item.job.work_location_latitude ?? null,
                        work_location_longitude: item.job.work_location_longitude ?? null,
                        work_location_geocode_status: item.job.work_location_geocode_status ?? 'not_attempted',
                        internal_reference: item.job.internal_reference ?? null,
                        external_reference: item.job.external_reference ?? null,
                        number: item.job.number ?? null,
                        status_condition: item.job.status_condition ?? null,
                        status_name: item.job.status_name ?? null,
                        resolution: item.job.resolution ?? null,
                        main_description: item.job.main_description ?? null,
                        project_number: item.job.project_number ?? null,
                        project_name: item.job.project_name ?? null,
                        labels: item.job.labels ?? null,
                    }
                };

            // Create activity event object
            } else if (type === "activity") {
                event = {
                    ...base,
                    type: "activity",
                    activityId: id,
                    ...(recurrence_id && { recurrenceId: recurrence_id, occurrenceDate: occurrence_date }),
                    time_type_name: item.time_type_name ?? null,
                    note: item.note ?? null,
                    custom_color: item.custom_color ?? null
                };
            } else {
                continue;
            }

            // Add the new event object to the new event state
            newEvents[key] = event;

            // Add the resource id to the resource map
            if (!resourceMap[resId]) {
                resourceMap[resId] = [];
            }

            // Add the key of the event to the resource map
            resourceMap[resId].push(key);
        }
    }


    /*
    /* Keep existing and update new events
    */

    // Initialize variable to map the new event keys to the new fetched events
    const newEventIdMap: Record<string, string[]> = {};
    
    // Loop through the new event objects to generate new ids
    for (const event of Object.values(newEvents)) {
        let eventId: string;

        // Generate the event id based on the event type
        switch (event.type) {
            case "appointment":
                eventId = `appointment-${event.appointmentId}`;
                break;
            case "timerow":
                eventId = `timerow-${event.timerowId}`;
                break;
            case "activity":
                // A virtual occurrence has no id, the recurrence and date identify it instead
                eventId = event.recurrenceId
                    ? `activity-recurrence-${event.recurrenceId}-${event.occurrenceDate}`
                    : `activity-${event.activityId}`;
                break;
            default:
                continue;
        }

        // Add the event id to the event id map
        if (!newEventIdMap[eventId]) {
            newEventIdMap[eventId] = [];
        } 

        // Add the event key to the event id map
        newEventIdMap[eventId].push(event.key);
    }

    // Initialize merged events variable
    let mergedEvents: EventsListType;

    // Handle received events from delta update
    if (isDeltaUpdate) {

        // Recurrences whose id was touched in this delta, editing or
        // stopping a series can move or remove occurrences the batch no
        // longer carries under the exact same recurrence-and-date key
        const touchedRecurrenceIds = new Set(data.activity_recurrence_ids ?? []);

        // Initialize with the existing events
        mergedEvents = { ...existingEvents };

        // Loop through the existing events to remove the events that are in the response
        for (const [existingEventKey, existingEvent] of Object.entries(existingEvents)) {
            let shouldRemove = false;

            // Determine wheter the existing event should be removed
            switch (existingEvent.type) {
                case 'appointment':
                    shouldRemove = !!newEventIdMap[`appointment-${existingEvent.appointmentId}`];
                    break;
                case 'timerow':
                    shouldRemove = !!newEventIdMap[`timerow-${existingEvent.timerowId}`];
                    break;
                case 'activity':
                    // A touched recurrence resyncs all of its cached occurrences at once,
                    // a plain activity only removes when the same id reappears
                    shouldRemove = existingEvent.recurrenceId
                        ? touchedRecurrenceIds.has(existingEvent.recurrenceId)
                        : !!newEventIdMap[`activity-${existingEvent.activityId}`];
                    break;
                default:
                    continue;
            }

            // Remove the event from the existing state
            if (shouldRemove) {
                delete mergedEvents[existingEventKey];
            }
        }

        // Add the newly received event to the state
        for (const [newEventKey, newEvent] of Object.entries(newEvents)) {
            mergedEvents[newEventKey] = newEvent;
        }

    // Handle initial fetches
    } else {
        mergedEvents = { ...newEvents };

        // Loop through the existing events
        for (const [existingEventKey, existingEvent] of Object.entries(existingEvents)) {
            let eventId: string;

            // Get the existing event id based on the event type
            switch (existingEvent.type) {
                case "appointment":
                    eventId = `appointment-${existingEvent.appointmentId}`;
                    break;
                case "timerow":
                    eventId = `timerow-${existingEvent.timerowId}`;
                    break;
                case "activity":
                    eventId = `activity-${existingEvent.activityId}`;
                    break;
                default:
                    continue;
            }

            // Determine if the existing event falls into the fetched days period 
            const isInPeriod = currentFetchPeriod.has(existingEvent.dayKey);

            if (!isInPeriod) {
                mergedEvents[existingEventKey] = existingEvent;
            }
        }
    }


    /*
    /* Update events by resource state
    */

    // Initialize new events by resource variable
    const mergedEventsByResource: EventsByResourceType = {};

    // Loop through the merged events to refresh the resource map
    Object.values(mergedEvents).forEach(event => {

        // Get the resource id of the event
        const resId = event.resourceId;

        // Add the resource id to the events by resource list if it does not exist yet
        if (!mergedEventsByResource[resId]) {
            mergedEventsByResource[resId] = [];
        }

        // Add the event key to the events by resource list
        mergedEventsByResource[resId].push(event.key);
    });

    // Loop through the merged events to get the events which falls inside the current fetch period
    const currentFetchPeriodEvents: EventsListType = {};
    for (const [key, event] of Object.entries(mergedEvents)) {
        if (currentFetchPeriod.has(event.dayKey)) {
            currentFetchPeriodEvents[key] = event;
        }
    }

    // Loop through the merged events by resource to filter on the current fetch period
    const currentFetchPeriodEventsByResource: EventsByResourceType = {};
    for (const [resId, keys] of Object.entries(mergedEventsByResource)) {
        const filteredKeys = keys.filter(key => key in currentFetchPeriodEvents);
        if (filteredKeys.length) currentFetchPeriodEventsByResource[resId] = filteredKeys;
    }


    /*
    /* Merge and update event overlap
    */

    // Initialize variable to store updated event overlap map
    const updatedOverlapMap = new Map<string, EventType[]>();

    // Keep the existing event overlap values which fall outside the current fetch period
    for (const [dayKey, value] of existingOverlapMap.entries()) {
        if (!currentFetchPeriod.has(dayKey)) {
            updatedOverlapMap.set(dayKey, value);
        }
    }

    // Calculate the event overlap of the events of the current fetch period
    const overlappingEventsCurrentPeriod = calculateOverlappingEvents(
        resourceGroups,
        days,
        currentFetchPeriodEvents,
        currentFetchPeriodEventsByResource
    );

    // Merge the newly calculated event overlaps to the existing items
    for (const [dayKey, value] of overlappingEventsCurrentPeriod.entries()) {
        updatedOverlapMap.set(dayKey, value);
    }

    return {
        generatedEvents: mergedEvents,
        eventsByResource: mergedEventsByResource,
        updatedOverlapMap,
    };
};